import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { reportAnalystApi } from "@/lib/api";
import { AnalyticsEvents, track } from "@/lib/services/analytics";
import { useRealtimeRepair } from "@/lib/realtime/useRealtimeRepair";
import { incrementPollWonRepair } from "@/lib/realtime/pollWonMetrics";
import { onReportAnalystRunUpdated } from "@/lib/services/websocket";
import {
  isAnsweredAnalystStatus,
  isTerminalAnalystStatus,
} from "@digital-twins/shared-types";

import type {
  AnalystRunResponse,
  AnalystSessionResponse,
  AnalystSuggestionId,
  PersistedAnalystMessage,
} from "@digital-twins/shared-types";

/** Quiet period before a missed run-updated ping is repaired by one refetch. */
const ANALYST_RUN_STALL_MS = 20_000;
/** Bounds the post-timeout session read so a stalled GET cannot hold Retry. */
export const ANALYST_RECONCILE_TIMEOUT_MS = 5_000;

function isSubmitTimeout(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  const code = (error as { code?: string }).code;
  return code === "ECONNABORTED" || code === "ETIMEDOUT";
}

function createUuid(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    (character) => {
      const random = Math.floor(Math.random() * 16);
      const value = character === "x" ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    },
  );
}

interface AnalystSendEnvelope {
  requestToken: string;
  clientMessageToken: string;
  content: string;
  /** The suggestion chip the message came from; a retry sends it again. */
  suggestionId?: AnalystSuggestionId;
  /** App-drawn plot chrome follows the viewer's UI language. */
  uiLanguage?: string;
}

/** How a message was sent: from a suggestion chip, or a retry of an earlier envelope. */
export interface AnalystSendOptions {
  suggestionId?: AnalystSuggestionId;
  envelope?: AnalystSendEnvelope;
}

function isTerminalAssistantMessage(message: PersistedAnalystMessage): boolean {
  return message.role === "assistant" && isTerminalAnalystStatus(message.status);
}

function assistantSettlesSubmit(
  messages: Array<PersistedAnalystMessage>,
  requestToken: string,
): boolean {
  return messages.some(
    (message) =>
      message.requestToken === requestToken &&
      isTerminalAssistantMessage(message),
  );
}

/**
 * A snapshot regresses a settled submit when that request token is still in
 * the incoming thread but its assistant is no longer terminal. Settlement
 * lives on the token set and on the messages already committed, so a stale
 * pending body cannot replace either one.
 *
 * Absence is not a regression. A reset deletes the old thread, and a hydrate
 * that started after the settled snapshot may replace it. A hydrate that
 * started earlier is still a stale read and must not drop the turn.
 */
function snapshotRegressesSettledSubmit(
  incoming: Array<PersistedAnalystMessage>,
  committed: Array<PersistedAnalystMessage>,
  settledRequestTokens: ReadonlySet<string>,
  hydrateSequence: number,
  committedHydrateSequence: number,
): boolean {
  const tokens = new Set<string>(settledRequestTokens);
  for (const message of committed) {
    if (message.requestToken && isTerminalAssistantMessage(message)) {
      tokens.add(message.requestToken);
    }
  }
  const startedAfterCommit = hydrateSequence > committedHydrateSequence;
  for (const requestToken of tokens) {
    const mentioned = incoming.some(
      (message) => message.requestToken === requestToken,
    );
    if (!mentioned) {
      if (startedAfterCommit) {
        continue;
      }
      return true;
    }
    const stillTerminal = incoming.some(
      (message) =>
        message.requestToken === requestToken &&
        isTerminalAssistantMessage(message),
    );
    if (!stillTerminal) {
      return true;
    }
  }
  return false;
}

function createOptimisticMessage(
  envelope: AnalystSendEnvelope,
): PersistedAnalystMessage {
  const now = new Date().toISOString();
  return {
    token: envelope.clientMessageToken,
    requestToken: envelope.requestToken,
    clientMessageToken: envelope.clientMessageToken,
    role: "user",
    status: "complete",
    content: envelope.content,
    suggestionId: envelope.suggestionId ?? null,
    evidence: [],
    charts: [],
    tables: [],
    errorMessage: null,
    responseKind: null,
    failureCode: null,
    caveats: [],
    createdAt: now,
    updatedAt: now,
  };
}

export function useAnalystTab(experimentId: string) {
  const { t, i18n } = useTranslation("app");
  const [messages, setMessages] = useState<Array<PersistedAnalystMessage>>([]);
  // The storyline revision the thread started on (the session records it).
  const [storylineRevision, setStorylineRevision] = useState<string | null>(null);
  const [activeRuns, setActiveRuns] = useState<Array<AnalystRunResponse>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isReconciling, setIsReconciling] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [canRetrySend, setCanRetrySend] = useState(false);
  const experimentIdRef = useRef(experimentId);
  const transportRetryRef = useRef<AnalystSendEnvelope | null>(null);
  /**
   * Request tokens whose assistant turn already reached a terminal status.
   * Settlement is a property of the token, recorded from any committed
   * snapshot, including a hydrate that runs before a retry envelope exists.
   * A later transport timeout must not roll that token back to Retry.
   */
  const settledRequestTokensRef = useRef<Set<string>>(new Set());
  const messagesRef = useRef<Array<PersistedAnalystMessage>>([]);
  const activeRunsRef = useRef(activeRuns);
  activeRunsRef.current = activeRuns;
  const requestGenerationRef = useRef(0);
  /**
   * Start order of session reads. Concurrent hydrates share one request
   * generation, so generation alone cannot order them. A read becomes
   * obsolete only after a newer read commits a valid snapshot. A newer
   * read that fails leaves the committed cursor where it is, so the older
   * success can still land.
   */
  const hydrateSequenceRef = useRef(0);
  const committedHydrateSequenceRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isSendingRef = useRef(false);
  const isReconcilingRef = useRef(false);

  experimentIdRef.current = experimentId;

  const isCurrentRequest = useCallback(
    (requestGeneration: number) =>
      requestGenerationRef.current === requestGeneration &&
      experimentIdRef.current === experimentId,
    [experimentId],
  );

  const invalidateRequests = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    requestGenerationRef.current += 1;
    return {
      requestGeneration: requestGenerationRef.current,
      signal: abortControllerRef.current.signal,
    };
  }, []);

  const rememberSettledSubmits = useCallback(
    (persistedMessages: Array<PersistedAnalystMessage>) => {
      for (const message of persistedMessages) {
        if (!message.requestToken || !isTerminalAssistantMessage(message)) {
          continue;
        }
        settledRequestTokensRef.current.add(message.requestToken);
      }
      const retry = transportRetryRef.current;
      if (!retry || !settledRequestTokensRef.current.has(retry.requestToken)) {
        return;
      }
      transportRetryRef.current = null;
      setSendError(null);
      setCanRetrySend(false);
    },
    [],
  );

  const isRequestSettled = useCallback((requestToken: string) => {
    return (
      settledRequestTokensRef.current.has(requestToken) ||
      assistantSettlesSubmit(messagesRef.current, requestToken)
    );
  }, []);

  const releaseTransportRetry = useCallback(() => {
    transportRetryRef.current = null;
    setSendError(null);
    setCanRetrySend(false);
  }, []);

  /**
   * POST acceptance is a snapshot, but it is not a hydrate. Allocate a
   * sequence strictly after every session read that has already started so
   * those reads cannot replace the accepted pending run. A read that starts
   * afterward is newer and may still commit, which is how a reset lands.
   */
  const sealAcceptedSnapshot = useCallback(() => {
    const barrier = hydrateSequenceRef.current + 1;
    hydrateSequenceRef.current = barrier;
    committedHydrateSequenceRef.current = barrier;
  }, []);

  const hydrateSession = useCallback(
    async (
      requestGeneration: number,
      showLoading: boolean,
      signal?: AbortSignal,
      options?: { suppressLoadError?: boolean; timeoutMs?: number },
    ): Promise<AnalystSessionResponse | null> => {
      const hydrateSequence = hydrateSequenceRef.current + 1;
      hydrateSequenceRef.current = hydrateSequence;
      if (showLoading) {
        setIsLoading(true);
      }
      try {
        const response = await reportAnalystApi.loadOrCreateSession(
          experimentId,
          signal,
          options?.timeoutMs,
        );
        if (signal?.aborted || !isCurrentRequest(requestGeneration)) {
          return null;
        }
        // The reconciliation read and the socket read share this generation.
        // An earlier start must not land after a later start has committed a
        // valid snapshot. A later start that fails does not commit, so the
        // earlier success still can. A body that turns an already-terminal
        // assistant back into a pending one must not land at all: that
        // leaves a spinner with no run for stall repair to refetch.
        if (hydrateSequence < committedHydrateSequenceRef.current) {
          return null;
        }
        if (
          snapshotRegressesSettledSubmit(
            response.session.messages,
            messagesRef.current,
            settledRequestTokensRef.current,
            hydrateSequence,
            committedHydrateSequenceRef.current,
          )
        ) {
          return null;
        }
        committedHydrateSequenceRef.current = hydrateSequence;
        setMessages(response.session.messages);
        messagesRef.current = response.session.messages;
        setStorylineRevision(response.session.storylineRevision);
        setActiveRuns(response.activeRuns);
        setError(null);
        // Record terminal tokens before any retry envelope exists. A submit
        // can still be awaiting its POST while this snapshot lands.
        rememberSettledSubmits(response.session.messages);
        return response;
      } catch (requestError) {
        if (!isCurrentRequest(requestGeneration)) {
          return null;
        }
        if (
          requestError instanceof DOMException &&
          requestError.name === "AbortError"
        ) {
          return null;
        }
        if (options?.suppressLoadError) {
          return null;
        }
        setError(t("studyReport.analyst.loadError"));
        track(AnalyticsEvents.REPORT_ANALYST_ERROR, {
          experiment_id: experimentId,
        });
        return null;
      } finally {
        if (isCurrentRequest(requestGeneration)) {
          setIsLoading(false);
        }
      }
    },
    [experimentId, isCurrentRequest, rememberSettledSubmits, t],
  );

  const loadSession = useCallback(async () => {
    const { requestGeneration, signal } = invalidateRequests();
    await hydrateSession(requestGeneration, true, signal);
  }, [hydrateSession, invalidateRequests]);

  useEffect(() => {
    const { requestGeneration, signal } = invalidateRequests();
    isSendingRef.current = false;
    setMessages([]);
    setStorylineRevision(null);
    messagesRef.current = [];
    settledRequestTokensRef.current.clear();
    setActiveRuns([]);
    setIsLoading(true);
    setIsSending(false);
    setIsReconciling(false);
    isReconcilingRef.current = false;
    setIsResetting(false);
    setError(null);
    setSendError(null);
    setCanRetrySend(false);
    transportRetryRef.current = null;
    void hydrateSession(requestGeneration, false, signal);

    return () => {
      if (requestGenerationRef.current === requestGeneration) {
        abortControllerRef.current?.abort();
        requestGenerationRef.current += 1;
      }
    };
  }, [experimentId, hydrateSession, invalidateRequests]);

  const pollActiveRuns = useCallback(async () => {
    if (activeRuns.length === 0) {
      return;
    }
    const requestGeneration = requestGenerationRef.current;
    const signal = abortControllerRef.current?.signal;
    try {
      const runs = await Promise.all(
        activeRuns.map((run) =>
          reportAnalystApi.getRun(experimentId, run.token, signal),
        ),
      );
      if (!isCurrentRequest(requestGeneration)) {
        return;
      }
      const hasTerminalRun = runs.some((run) =>
        isTerminalAnalystStatus(run.status),
      );
      if (hasTerminalRun) {
        const hasCompletedRun = runs.some((run) =>
          isAnsweredAnalystStatus(run.status),
        );
        track(
          hasCompletedRun
            ? AnalyticsEvents.REPORT_ANALYST_CUSTOM_ANALYSIS_COMPLETED
            : AnalyticsEvents.REPORT_ANALYST_ERROR,
          { experiment_id: experimentId },
        );
        await hydrateSession(requestGeneration, false, signal);
      }
    } catch (requestError) {
      if (isCurrentRequest(requestGeneration)) {
        if (
          requestError instanceof DOMException &&
          requestError.name === "AbortError"
        ) {
          return;
        }
        setError(t("studyReport.analyst.pollError"));
        track(AnalyticsEvents.REPORT_ANALYST_ERROR, {
          experiment_id: experimentId,
        });
      }
    }
  }, [activeRuns, experimentId, hydrateSession, isCurrentRequest, t]);

  useEffect(() => {
    if (activeRuns.length === 0) {
      return;
    }
    void pollActiveRuns();
  }, [activeRuns, pollActiveRuns]);

  const { notifyActivity } = useRealtimeRepair({
    active: activeRuns.length > 0,
    stallTimeoutMs: ANALYST_RUN_STALL_MS,
    onRepair: (reason) => {
      incrementPollWonRepair("ANALYST", reason);
      void pollActiveRuns();
    },
  });

  useEffect(() => {
    return onReportAnalystRunUpdated(experimentId, (event) => {
      if (!isTerminalAnalystStatus(event.status)) {
        return;
      }
      notifyActivity();
      const requestGeneration = requestGenerationRef.current;
      const signal = abortControllerRef.current?.signal;
      void hydrateSession(requestGeneration, false, signal);
    });
  }, [experimentId, hydrateSession, notifyActivity]);

  const reconcileSubmit = useCallback(
    async (
      requestGeneration: number,
      envelope: AnalystSendEnvelope,
      signal: AbortSignal | undefined,
    ): Promise<"landed" | "unresolved"> => {
      const controller = new AbortController();
      const abortFromParent = () => {
        controller.abort();
      };
      if (signal) {
        if (signal.aborted) {
          controller.abort();
        } else {
          signal.addEventListener("abort", abortFromParent);
        }
      }
      const timeoutId = window.setTimeout(() => {
        controller.abort();
      }, ANALYST_RECONCILE_TIMEOUT_MS);
      const boundedRefresh = new Promise<null>((resolve) => {
        const timer = window.setTimeout(() => {
          resolve(null);
        }, ANALYST_RECONCILE_TIMEOUT_MS);
        controller.signal.addEventListener("abort", () => {
          window.clearTimeout(timer);
          resolve(null);
        });
      });
      try {
        const refreshed = await Promise.race([
          hydrateSession(requestGeneration, false, controller.signal, {
            suppressLoadError: true,
            timeoutMs: ANALYST_RECONCILE_TIMEOUT_MS,
          }),
          boundedRefresh,
        ]);
        if (!refreshed || !isCurrentRequest(requestGeneration)) {
          return "unresolved";
        }
        const stillActive = refreshed.activeRuns.some(
          (run) =>
            run.requestToken === envelope.requestToken &&
            !isTerminalAnalystStatus(run.status),
        );
        if (
          stillActive ||
          assistantSettlesSubmit(
            refreshed.session.messages,
            envelope.requestToken,
          )
        ) {
          return "landed";
        }
        return "unresolved";
      } finally {
        window.clearTimeout(timeoutId);
        signal?.removeEventListener("abort", abortFromParent);
      }
    },
    [hydrateSession, isCurrentRequest],
  );

  const sendMessage = useCallback(
    async (content: string, options: AnalystSendOptions = {}) => {
      const { envelope } = options;
      const trimmedContent = (envelope?.content ?? content).trim();
      if (
        !trimmedContent ||
        isSendingRef.current ||
        isReconcilingRef.current ||
        activeRunsRef.current.length > 0
      ) {
        return;
      }

      const requestToken = envelope?.requestToken ?? createUuid();
      const clientMessageToken = envelope?.clientMessageToken ?? createUuid();
      const uiLanguage = envelope?.uiLanguage ?? i18n.language;
      const suggestionId = envelope ? envelope.suggestionId : options.suggestionId;
      const sendEnvelope: AnalystSendEnvelope = {
        requestToken,
        clientMessageToken,
        content: trimmedContent,
        ...(suggestionId ? { suggestionId } : {}),
        ...(uiLanguage ? { uiLanguage } : {}),
      };
      const optimisticMessage = createOptimisticMessage(sendEnvelope);

      isSendingRef.current = true;
      let requestGeneration = requestGenerationRef.current;
      let signal = abortControllerRef.current?.signal;
      if (!signal) {
        const invalidated = invalidateRequests();
        requestGeneration = invalidated.requestGeneration;
        signal = invalidated.signal;
      }
      setIsSending(true);
      setError(null);
      setSendError(null);
      setCanRetrySend(false);
      setMessages((current) => [
        ...current.filter(
          (message) =>
            message.clientMessageToken !== clientMessageToken &&
            message.requestToken !== requestToken,
        ),
        optimisticMessage,
      ]);
      track(AnalyticsEvents.REPORT_ANALYST_MESSAGE_SENT, {
        experiment_id: experimentId,
      });

      try {
        const response = await reportAnalystApi.submitMessage(
          experimentId,
          sendEnvelope,
          signal,
        );
        if (!isCurrentRequest(requestGeneration)) {
          return;
        }
        releaseTransportRetry();
        // The pending body is mapped before the turn starts. A terminal
        // hydrate can land while this POST is still in flight and record the
        // request token as settled. Committing the stale pending assistant
        // and run would replace that snapshot and bring the spinner back.
        if (isRequestSettled(sendEnvelope.requestToken)) {
          return;
        }
        const committedMessages = [
          response.userMessage,
          ...(response.assistantMessage ? [response.assistantMessage] : []),
        ];
        setMessages((current) => {
          const nextMessages = [
            ...current.filter(
              (message) =>
                message.clientMessageToken !== clientMessageToken &&
                message.requestToken !== requestToken,
            ),
            ...committedMessages,
          ];
          messagesRef.current = nextMessages;
          return nextMessages;
        });
        rememberSettledSubmits(committedMessages);
        if (response.run && !isTerminalAnalystStatus(response.run.status)) {
          const activeRun = response.run;
          setActiveRuns((current) => [
            ...current.filter((run) => run.token !== activeRun.token),
            activeRun,
          ]);
          track(AnalyticsEvents.REPORT_ANALYST_CUSTOM_ANALYSIS_PENDING, {
            experiment_id: experimentId,
          });
        }
        sealAcceptedSnapshot();
      } catch (requestError) {
        if (!isCurrentRequest(requestGeneration)) {
          return;
        }
        if (
          requestError instanceof DOMException &&
          requestError.name === "AbortError"
        ) {
          return;
        }
        isSendingRef.current = false;
        setIsSending(false);
        // The socket can hydrate a terminal assistant while this POST is still
        // pending, before any retry envelope exists. That settlement has to
        // survive the timeout: installing an envelope and reconciling would
        // let the 5s fallback delete the answer and show Retry.
        if (isRequestSettled(sendEnvelope.requestToken)) {
          releaseTransportRetry();
          return;
        }
        transportRetryRef.current = sendEnvelope;
        isReconcilingRef.current = true;
        setIsReconciling(true);
        let outcome: "landed" | "unresolved" = "unresolved";
        try {
          outcome = await reconcileSubmit(
            requestGeneration,
            sendEnvelope,
            signal,
          );
        } catch {
          outcome = "unresolved";
        } finally {
          isReconcilingRef.current = false;
          setIsReconciling(false);
        }
        if (!isCurrentRequest(requestGeneration)) {
          return;
        }
        if (
          outcome === "landed" ||
          isRequestSettled(sendEnvelope.requestToken)
        ) {
          releaseTransportRetry();
          return;
        }
        // This continuation outlives the GET it started. A later snapshot can
        // retire the envelope before the timeout fallback returns "unresolved".
        // Restoring is only valid while this submit still owns the retry slot
        // and the token has not settled. Otherwise the fallback deletes the
        // hydrated assistant and resurrects Retry.
        if (
          transportRetryRef.current?.requestToken !== sendEnvelope.requestToken
        ) {
          return;
        }
        setMessages((current) => {
          const nextMessages = [
            ...current.filter(
              (message) =>
                message.clientMessageToken !== clientMessageToken &&
                message.requestToken !== requestToken,
            ),
            optimisticMessage,
          ];
          messagesRef.current = nextMessages;
          return nextMessages;
        });
        setSendError(
          t(
            isSubmitTimeout(requestError)
              ? "studyReport.analyst.sendTimeout"
              : "studyReport.analyst.sendError",
          ),
        );
        setCanRetrySend(true);
        track(AnalyticsEvents.REPORT_ANALYST_ERROR, {
          experiment_id: experimentId,
        });
      } finally {
        if (isCurrentRequest(requestGeneration)) {
          isSendingRef.current = false;
          setIsSending(false);
        }
      }
    },
    [
      experimentId,
      i18n.language,
      invalidateRequests,
      isCurrentRequest,
      isRequestSettled,
      sealAcceptedSnapshot,
      reconcileSubmit,
      releaseTransportRetry,
      rememberSettledSubmits,
      t,
    ],
  );

  const retryLastSend = useCallback(() => {
    const envelope = transportRetryRef.current;
    if (
      !envelope ||
      isSendingRef.current ||
      isReconcilingRef.current ||
      activeRunsRef.current.length > 0
    ) {
      return;
    }
    setSendError(null);
    setCanRetrySend(false);
    void sendMessage(envelope.content, { envelope });
  }, [sendMessage]);

  const retryMessage = useCallback(
    (requestToken: string | null) => {
      if (!requestToken) {
        return;
      }
      // The failed turn stays on screen until a new send actually starts.
      // Removing it first would hide the persisted failure when send is
      // blocked, and the next hydrate would put the same rows back.
      if (
        isSendingRef.current ||
        isReconcilingRef.current ||
        activeRunsRef.current.length > 0
      ) {
        return;
      }
      const userMessage = messages.find(
        (message) =>
          message.requestToken === requestToken && message.role === "user",
      );
      const content = userMessage?.content.trim() ?? "";
      if (!userMessage || !content) {
        return;
      }
      setMessages((current) => {
        const nextMessages = current.filter(
          (message) => message.requestToken !== requestToken,
        );
        messagesRef.current = nextMessages;
        return nextMessages;
      });
      void sendMessage(content, {
        suggestionId: userMessage.suggestionId ?? undefined,
      });
    },
    [messages, sendMessage],
  );

  const resetThread = useCallback(async () => {
    // Submit, reconciliation, and an active run all own the turn. Resetting
    // in any of those windows races the POST or deletes the row while Python
    // is still running. The server also rejects reset while a run is
    // pending or running.
    if (
      isSendingRef.current ||
      isReconcilingRef.current ||
      activeRunsRef.current.length > 0
    ) {
      return;
    }
    const { requestGeneration, signal } = invalidateRequests();
    isSendingRef.current = false;
    setIsSending(false);
    setSendError(null);
    setCanRetrySend(false);
    setIsResetting(true);
    try {
      const response = await reportAnalystApi.resetThread(experimentId, signal);
      if (!isCurrentRequest(requestGeneration)) {
        return;
      }
      setMessages(response.session.messages);
      setStorylineRevision(response.session.storylineRevision);
      messagesRef.current = response.session.messages;
      settledRequestTokensRef.current.clear();
      rememberSettledSubmits(response.session.messages);
      setActiveRuns(response.activeRuns);
      // The reset response is the new thread. A session read that already
      // started, including a terminal socket event during this request, can
      // still resolve with the deleted messages. Seal those reads out. A
      // read that starts after this response may commit the reset thread.
      sealAcceptedSnapshot();
      setError(null);
      track(AnalyticsEvents.REPORT_ANALYST_RESET, {
        experiment_id: experimentId,
      });
    } catch (requestError) {
      if (isCurrentRequest(requestGeneration)) {
        if (
          requestError instanceof DOMException &&
          requestError.name === "AbortError"
        ) {
          return;
        }
        setError(t("studyReport.analyst.resetError"));
        track(AnalyticsEvents.REPORT_ANALYST_ERROR, {
          experiment_id: experimentId,
        });
      }
    } finally {
      if (isCurrentRequest(requestGeneration)) {
        setIsResetting(false);
      }
    }
  }, [
    experimentId,
    invalidateRequests,
    isCurrentRequest,
    rememberSettledSubmits,
    sealAcceptedSnapshot,
    t,
  ]);

  return {
    messages,
    storylineRevision,
    isLoading,
    isSending,
    isReconciling,
    isResetting,
    isPolling: activeRuns.length > 0,
    error,
    sendError,
    canRetrySend,
    loadSession,
    sendMessage,
    retryLastSend,
    retryMessage,
    resetThread,
  };
}
